import type { LitElement } from "lit";
import { effect } from "@preact/signals-core";

// Mixin that makes a LitElement re-render whenever a signal it read during
// render changes. Wraps `performUpdate` in a signals `effect()` so reads are
// tracked, then translates re-triggers into `requestUpdate()`.
//
// Usage:
//   class LayerPanel extends SignalWatcher(LitElement) { ... }

type Constructor<T> = new (...args: any[]) => T;

// Return type is annotated as `T` so declaration emit sees an alias of the
// base class, not the anonymous mixin class. Without this, dts emission would
// fail for every component that extends SignalWatcher(LitElement) because
// LitElement has private members that can't be re-emitted from an anonymous
// class the tool doesn't own.
export function SignalWatcher<T extends Constructor<LitElement>>(Base: T): T {
  return class extends Base {
    // Stored on a per-instance property so we can tear it down. Named with a
    // Symbol-esque underscore prefix so it doesn't collide with any Lit
    // internal names.
    _sw_dispose: (() => void) | null = null;

    override performUpdate(): void {
      this._sw_dispose?.();
      let ranSync = false;
      this._sw_dispose = effect(() => {
        if (!ranSync) {
          // First call runs synchronously inside effect(). Do the actual
          // render here so signal reads inside render() are tracked.
          ranSync = true;
          super.performUpdate();
        } else {
          // A tracked signal changed after the initial render. Ask Lit to
          // schedule another cycle; the new performUpdate() call will set up
          // a fresh effect and dispose this one.
          this.requestUpdate();
        }
      });
    }

    override disconnectedCallback(): void {
      super.disconnectedCallback();
      this._sw_dispose?.();
      this._sw_dispose = null;
    }
  } as T;
}
